'use client';

import { LayoutContainer, Modal } from '@petsmart-ui/sparky';
import { useEffect, useRef } from 'react';
import { A11y, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperRef } from 'swiper/types';

import type { MediaEntityType } from '@/entities/product';

import { A11Y_OPTIONS } from '../../lib';
import { useGalleryStore } from '../../model';
import { GalleryPagination } from '../GalleryPagination';
import { ImageSlide } from '../ImageSlide';
import { VideoSlide } from '../VideoSlide';

import 'swiper/css/navigation';
import 'swiper/css';
import 'swiper/css/a11y';
import './GalleryModal.scss';

export type GalleryModalProps = Readonly<{
    media: MediaEntityType[];
    productName: string;
}>;

const navigationOptions = {
    addIcons: false,
    nextEl: `#gallery-modal .pdp-gallery__next`,
    prevEl: `#gallery-modal .pdp-gallery__prev`,
};

export const GalleryModal = ({ media, productName }: GalleryModalProps) => {
    const isOpen = useGalleryStore(state => state.isModalOpen);
    const closeModal = useGalleryStore(state => state.closeModal);
    const initialModalIndex = useGalleryStore(state => state.initialModalIndex);
    const swiperRef = useRef<SwiperRef | null>(null);

    const handleSwiperReady = (swiper: SwiperRef) => {
        swiperRef.current = swiper;
    };

    const handleBulletClick = (index: number) => {
        swiperRef.current?.slideTo(index);
    };

    useEffect(() => {
        if (isOpen && swiperRef.current) {
            swiperRef.current.slideTo(initialModalIndex, 0);
        }
    }, [isOpen, initialModalIndex]);

    return (
        <Modal dismissible fullWidth isActive={isOpen} onClose={closeModal} className="pdp-gallery__modal-container">
            <Modal.Header className="pdp-gallery__modal-header" />
            <Modal.Body id="gallery-modal-body" className="pdp-gallery__modal-body">
                <LayoutContainer variant="narrow" data-testid="gallery-modal-layout">
                    <div data-testid="test-gallery-modal" id="gallery-modal" className="pdp-gallery">
                        <Swiper
                            spaceBetween={10}
                            slidesPerView={1}
                            modules={[A11y, Navigation]}
                            a11y={A11Y_OPTIONS}
                            onSwiper={handleSwiperReady}
                            navigation={navigationOptions}
                        >
                            {media.map((item, index) => {
                                return (
                                    <SwiperSlide key={index}>
                                        {item.type === 'image' && (
                                            <ImageSlide item={item} productName={productName} index={index} />
                                        )}

                                        {item.type === 'video' && <VideoSlide video={item} index={index} />}
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>

                        <div
                            className="pdp-gallery__prev swiper-button-prev swiper-button-disabled"
                            data-testid="gallery-modal-prev"
                        />
                        <div className="pdp-gallery__next swiper-button-next" data-testid="gallery-modal-next" />

                        <div className="pdp-gallery__pagination" data-testid="gallery-modal-pagination">
                            <GalleryPagination
                                media={media}
                                productName={productName}
                                onBulletClick={handleBulletClick}
                            />
                        </div>
                    </div>
                </LayoutContainer>
            </Modal.Body>
        </Modal>
    );
};
